import { Button, Col, Flex, FormContainer, Modal, Row, Text } from '@wyz/ui';
import * as React from 'react';
import { NumberField, SelectField } from '~/common/components';
import { Trans, useI18n } from '~/common/context';
import {
  PRICING_CONFIGURATION_TYPE_LABEL_LOOKUP,
  SUPPLIER_TYPE_ID_TRANSLATIONS,
} from '~/common/translators';
import { DistributionPricingPolicyHelpers } from '~/purchase/helpers';
import {
  useActiveVehicleTypeGroups,
  useAdvancedExtraBonus,
} from '~/purchase/hooks';
import { PRICING_TYPE_ENUM } from '~/purchase/schemas';
import {
  IDistributionPricingPolicyBrand,
  IDistributionPricingPolicySeason,
  ISaveAdvancedExtraBonusesPayload,
} from '~/purchase/types';
/* eslint-disable @wyz/module/no-relative-import -- siblings are imported directly: going through the
   ~/purchase/components barrel would make this module depend on itself. */
import { AdvancedBonusBlock } from './advanced-bonus-block';
import './advanced-extra-bonus-modal.scss';
/* eslint-enable @wyz/module/no-relative-import */

const { advancedVehicleTypeColumns, bonusSign, carriedSupplierTypeIds } =
  DistributionPricingPolicyHelpers;

type Props = {
  open: boolean;
  brands: IDistributionPricingPolicyBrand[];
  seasons: IDistributionPricingPolicySeason[];
  /** The brand whose bonus is being edited, null when a new one is being entered. */
  editedBrand?: IDistributionPricingPolicyBrand | null;
  isSaving?: boolean;
  onClose: () => void;
  onSave: (payload: ISaveAdvancedExtraBonusesPayload) => Promise<unknown>;
};

/**
 * Enters one bonus at a time, for several brands and sources at once. The HQ bonus stays a property of
 * each brand: the multiple selection only saves the typing, it does not turn it into a shared bonus.
 *
 * Opened from the pencil of a brand, it edits the bonus that brand holds: the brand itself is fixed,
 * while the sources stay open so the bonus can be extended to another supplier type.
 */
export function AdvancedExtraBonusModal({
  open,
  brands,
  seasons,
  editedBrand = null,
  isSaving = false,
  onClose,
  onSave,
}: Props) {
  const { t } = useI18n();
  // Vehicle types are the ones of the default group, as every pricing policy screen reads them.
  const vehicleTypeGroups = useActiveVehicleTypeGroups();
  // Memoised: the columns feed the reset of the form, so rebuilding them on every render would make
  // that reset fire on every render too.
  const groups = vehicleTypeGroups.data?.data;
  const vehicleTypes = React.useMemo(
    () => advancedVehicleTypeColumns(groups ?? [], t),
    [groups, t],
  );
  const { methods, draft, submit, clear, keepEntering } = useAdvancedExtraBonus(
    {
      seasons,
      vehicleTypes,
      editedBrand,
      onSave,
      onSaved: onClose,
    },
  );

  const bonusTypeOptions = Object.values(PRICING_TYPE_ENUM).map((value) => ({
    value,
    label: t(PRICING_CONFIGURATION_TYPE_LABEL_LOOKUP[value]),
  }));

  const brandOptions = brands.map(({ brand }) => ({
    value: brand,
    label: brand,
  }));

  // The intersection, not the union: see `carriedSupplierTypeIds`.
  const selectedBrands = draft.brands ?? [];
  const supplierTypeOptions = React.useMemo(
    () =>
      carriedSupplierTypeIds(brands, selectedBrands).map((supplierTypeId) => ({
        value: supplierTypeId,
        label: SUPPLIER_TYPE_ID_TRANSLATIONS[supplierTypeId]
          ? t(SUPPLIER_TYPE_ID_TRANSLATIONS[supplierTypeId])
          : String(supplierTypeId),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the brand list is compared by content.
    [brands, selectedBrands.join('|'), t],
  );

  // Narrowing the brands can strand a source that no longer has a line. Only a change the user made
  // prunes it: the reset that opens the screen in edition must keep the sources it just read back.
  const previousBrands = React.useRef<string[] | null>(null);

  React.useEffect(() => {
    const previous = previousBrands.current;
    previousBrands.current = selectedBrands;

    if (null === previous || previous.join('|') === selectedBrands.join('|')) {
      return;
    }

    const carried = carriedSupplierTypeIds(brands, selectedBrands);
    const kept = (draft.supplierTypeIds ?? []).filter((supplierTypeId) =>
      carried.includes(supplierTypeId),
    );

    if (kept.length !== (draft.supplierTypeIds ?? []).length) {
      methods.setValue('supplierTypeIds', kept);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- driven by the brand selection alone.
  }, [selectedBrands.join('|')]);

  const close = () => {
    clear();
    onClose();
  };

  return (
    <Modal.Container open={open} onClose={close} size='xl' centered>
      <Modal.Header>
        <Modal.Title>
          <Text color='tertiary-gray' weight='bold' heading='normal'>
            <Trans
              i18nKey={
                editedBrand
                  ? 'app.distribution.pricing_policy.detail.advanced.edit_title'
                  : 'app.distribution.pricing_policy.detail.advanced.title'
              }
            />
          </Text>
        </Modal.Title>
      </Modal.Header>

      <FormContainer methods={methods} submitHandler={submit}>
        <Modal.Body>
          <div className='advanced-extra-bonus'>
            <Text component='p' color='tertiary-gray' weight='bold'>
              <Trans i18nKey='app.distribution.pricing_policy.detail.advanced.by_brands' />
            </Text>
            <Row>
              <Col md={6}>
                <SelectField
                  name='brands'
                  size='sm'
                  isMulti
                  // The pencil edits the bonus of one brand: retargeting it would write on brands the
                  // user never opened.
                  isDisabled={Boolean(editedBrand)}
                  options={brandOptions}
                  label={
                    <Trans i18nKey='app.distribution.pricing_policy.detail.advanced.brands' />
                  }
                  wrapItems
                  maxShown={10}
                  required
                />
              </Col>
              <Col md={6}>
                <SelectField
                  name='supplierTypeIds'
                  size='sm'
                  isMulti
                  // No brand, no line to read the sources from.
                  isDisabled={0 === selectedBrands.length}
                  options={supplierTypeOptions}
                  label={
                    <Trans i18nKey='app.distribution.pricing_policy.detail.advanced.sources' />
                  }
                  wrapItems
                  maxShown={Infinity}
                  required
                />
              </Col>
            </Row>

            <Text
              component='p'
              color='tertiary-gray'
              weight='bold'
              className='mt-3'
            >
              <Trans i18nKey='app.distribution.pricing_policy.detail.column.bonus_hq' />
            </Text>
            <Row className='align-items-center'>
              <Col md={6}>
                <SelectField
                  name='hqBonusType'
                  size='sm'
                  isSearchable={false}
                  options={bonusTypeOptions}
                  label={
                    <Trans i18nKey='app.distribution.pricing_policy.detail.advanced.hq_configuration' />
                  }
                  required
                />
              </Col>
              <Col md={6}>
                <NumberField
                  name='hqBonus'
                  label=' '
                  size='sm'
                  withKnobs={false}
                  sign={bonusSign(draft.hqBonusType)}
                  feedbackType='tooltip'
                />
              </Col>
            </Row>

            <Text
              component='p'
              color='tertiary-gray'
              weight='bold'
              className='mt-3'
            >
              <Trans i18nKey='app.distribution.pricing_policy.detail.column.extra_bonus' />
            </Text>

            <AdvancedBonusBlock
              draft={draft}
              seasons={seasons}
              vehicleTypes={vehicleTypes}
              bonusTypeOptions={bonusTypeOptions}
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Flex className='space-x-2 w-100'>
            <Button
              id='cancel-advanced-bonus'
              type='button'
              variant='secondary'
              fullWidth
              onClick={close}
              disabled={isSaving}
            >
              <Trans i18nKey='app.common.button.cancel' />
            </Button>
            {!editedBrand && (
              <Button
                id='save-and-new-advanced-bonus'
                type='submit'
                variant='primary'
                fullWidth
                disabled={isSaving}
                onClick={keepEntering}
              >
                <Trans i18nKey='app.distribution.pricing_policy.detail.advanced.validate_and_new' />
              </Button>
            )}
            <Button
              id='save-advanced-bonus'
              type='submit'
              variant='primary'
              fullWidth
              disabled={isSaving}
            >
              <Trans i18nKey='app.common.buttons.validate' />
            </Button>
          </Flex>
        </Modal.Footer>
      </FormContainer>
    </Modal.Container>
  );
}
